import React, { useContext } from 'react';
import { graphql } from 'gatsby';

type RawTranslationType = {
  nodes: [{ [k: string]: string }];
};

export type TranslationType = {
  [k: string]: string;
};

export type TranslationVars = {
  [k: string]: string | number | null;
};

const translationContext = React.createContext(
  {} as { translations?: TranslationType; locale: string },
);

export const TranslationProvider = ({
  value: { translations, locale },
  children,
}: {
  value: { translations: RawTranslationType; locale: string };
  children: React.ReactNode;
}) => {
  const trans = translations?.nodes.reduce<TranslationType>(
    (acc, v) => ({ [v.key]: v.value, ...acc }),
    {},
  );

  return (
    <translationContext.Provider value={{ translations: trans, locale }}>
      {children}
    </translationContext.Provider>
  );
};

export default () => {
  const { translations, locale } = useContext(translationContext);

  const exists = (key: string) => {
    if (!translations || !(key in translations) || translations[key] === null) {
      if (process.env.GATSBY_ENV !== 'production') {
        console.warn(
          translations ? `Missing translation key : ${key}` : 'Missing translation injection',
        );
      }
      return false;
    }
    return true;
  };

  const t = (key: string, vars?: TranslationVars) => {
    if (!translations || !exists(key)) {
      return key;
    }

    return vars
      ? Object.entries(vars).reduce((str, [entryKey, value]) => {
          return str.replaceAll(`{{${entryKey}}}`, value != null ? value.toString() : '');
        }, translations[key])
      : translations[key];
  };

  // Parses ICU plural syntax: {varName, plural, one {text} other {# text}}
  // then interpolates remaining {var} placeholders
  const tp = (key: string, count: number, vars?: TranslationVars): string => {
    if (!translations || !exists(key)) return key;

    let str = translations[key];

    // Resolve ICU plural blocks
    str = str.replace(/\{(\w+),\s*plural,\s*((?:\w+\s*\{[^{}]*\}\s*)+)\}/g, (_, varName, forms) => {
      const n = Number(vars?.[varName] ?? count);
      let rule = 'other';
      try {
        rule = new Intl.PluralRules(locale).select(n);
      } catch (_e) {
        /* ignore invalid locale */
      }

      const specific = new RegExp(`(?<!\\w)${rule}\\s*[{]([^{}]*)[}]`).exec(forms);
      const fallback = /other\s*\{([^{}]*)/.exec(forms);
      const text = (specific || fallback)?.[1]?.trim() ?? String(n);
      return text.replace(/#/g, String(n));
    });

    // Interpolate remaining {var} placeholders
    if (vars) {
      str = Object.entries(vars).reduce(
        (s, [k, v]) => s.replaceAll(`{${k}}`, v != null ? String(v) : ''),
        str,
      );
    }
    return str;
  };

  return {
    locale,
    exists,
    t,
    tp,
    t2: (key: string, vars?: TranslationVars) => (
      <span dangerouslySetInnerHTML={{ __html: t(key, vars) }} />
    ),
    getTranslatedName: (
      type: 'spot' | 'zone',
      item?: {
        code?: string;
        name?: string;
        shortName?: string;
      },
      shortName: boolean = false,
    ) => {
      const key = `${type}_${item?.code || ''}${shortName ? '-short' : ''}`;

      return translations && key in translations
        ? t(key)
        : (shortName ? item?.shortName : item?.name) || '';
    },
  };
};

export const query = graphql`
  fragment Translations on DatoCmsTranslationConnection {
    nodes {
      key
      value
    }
  }
`;
